import {Box, ThemeContainer, ThemeProvider} from '@dropbox/dig-foundations';
import {CloseLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {useCallback} from 'react';

import {ToolbarButton} from './ToolbarButton';

type ColorSelectionToolbarProps = {
  onColorSelect: (color: string) => void;
  onReset: () => void;
  isDarkMode: boolean;
};

// Color configuration with DIG color variables
const HIGHLIGHT_COLORS = [
  {
    name: 'Red',
    cssVar: 'var(--dig-color__highlight__red)',
    cssVarBtn: 'var(--dig-color__highlight__red--bolder)',
    key: 'red',
  },
  {
    name: 'Yellow',
    cssVar: 'var(--dig-color__highlight__yellow)',
    cssVarBtn: 'var(--dig-color__highlight__yellow--bolder)',
    key: 'yellow',
  },
  {
    name: 'Green',
    cssVar: 'var(--dig-color__highlight__green)',
    cssVarBtn: 'var(--dig-color__highlight__green--bolder)',
    key: 'green',
  },
  {
    name: 'Blue',
    cssVar: 'var(--dig-color__highlight__blue)',
    cssVarBtn: 'var(--dig-color__highlight__blue--bolder)',
    key: 'blue',
  },
  {
    name: 'Purple',
    cssVar: 'var(--dig-color__highlight__purple)',
    cssVarBtn: 'var(--dig-color__highlight__purple--bolder)',
    key: 'purple',
  },
];

// Simple color swatch component
const ColorSwatch = ({color, onClick}: {color: string; onClick: () => void}) => (
  <button
    onClick={onClick}
    // Prevent focus change so portal-based toolbar clicks do not collapse the active editor
    // selection before the highlight command runs.
    onMouseDown={(e) => e.preventDefault()}
    onPointerDown={(e) => e.preventDefault()}
    style={{
      width: 16,
      height: 16,
      margin: '0px 8px',
      borderRadius: '8px',
      backgroundColor: color,
      border: `0px solid ${color}`,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'transform 0.1s ease',
    }}
    onMouseEnter={(e) => {
      const target = e.currentTarget;
      target.style.transform = 'scale(1.1)';
    }}
    onMouseLeave={(e) => {
      const target = e.currentTarget;
      target.style.transform = 'scale(1)';
    }}
  />
);

export const ColorSelectionToolbar = ({
  onColorSelect,
  onReset,
  isDarkMode,
}: ColorSelectionToolbarProps) => {
  const handleColorClick = useCallback(
    (color: string) => {
      onColorSelect(color);
    },
    [onColorSelect]
  );

  const handleResetClick = useCallback(() => {
    onReset();
  }, [onReset]);

  return (
    <Box
      boxShadow="Floating"
      backgroundColor="Background Base"
      borderRadius="Large"
      paddingY="4"
      paddingX="8"
      display="flex"
      flexDirection="row"
      alignItems="center"
    >
      <ThemeProvider mode={isDarkMode ? 'dark' : 'bright'}>
        <ThemeContainer>
          {/* Color swatches */}
          {HIGHLIGHT_COLORS.map((colorConfig) => (
            <ColorSwatch
              key={colorConfig.key}
              color={colorConfig.cssVarBtn}
              onClick={() => handleColorClick(colorConfig.cssVar)}
            />
          ))}
        </ThemeContainer>
      </ThemeProvider>
      {/* Reset button */}
      <div
        style={{
          width: '1px',
          height: '24px',
          backgroundColor: 'var(--dig-color__text__base)',
          opacity: '0.2',
          margin: '0 8px',
        }}
      />
      <ToolbarButton title={t('Remove highlight')} onClick={handleResetClick} icon={CloseLine} />
    </Box>
  );
};
